import { WalletConfig } from "@/types/DocumentTemplate";
import { getStripAspect } from "../walletPassTypeSchemas";
import {
  BarcodeBlock,
  FieldRow,
  PassCard,
  PassHeader,
  StripWithPrimaryOverlay,
  passInsetX,
  useColors,
  useDeviceOnly,
  useRowCertainty,
  useVisibleCounts,
} from "./sharedPreviewBits";

type Props = {
  config: WalletConfig;
  currentLanguage: string;
};

export function CouponPreview({ config, currentLanguage }: Props) {
  const colors = useColors(config);
  const deviceOnly = useDeviceOnly();
  const certaintyOfPrimary = useRowCertainty("primaryFields");
  const certaintyOfSecondary = useRowCertainty("secondaryFields");
  const certaintyOfAuxiliary = useRowCertainty("auxiliaryFields");
  const visible = useVisibleCounts(config);

  return (
    <PassCard bg={colors.bg}>
      <PassHeader
        config={config}
        currentLanguage={currentLanguage}
        colors={colors}
      />

      {/* Apple overlays the offer (primary) field ON the strip — but only
          when there IS one. This lane called the overlay unconditionally while
          the event ticket and store card both gate on `hasStrip`, so a coupon
          with no artwork drew a 123px band with a black scrim over the pass's
          own background, and put its offer text in white on top of it: unread-
          able in the builder, invisible in the device view.

          The no-strip branch keeps the overlay's own size (`text-xl font-bold`,
          its default) rather than inventing one — nobody has measured a coupon
          without artwork on a device, and guessing is what this whole wave is
          cleaning up. */}
      {config.stripImageUrl ? (
        <StripWithPrimaryOverlay
          url={config.stripImageUrl}
          fields={config.primaryFields}
          certaintyOf={certaintyOfPrimary}
          currentLanguage={currentLanguage}
          colors={colors}
          aspect={getStripAspect(config)}
          visibleCount={visible.primary}
        />
      ) : (
        <>
          {/* The slot affordance survives the gate. Gating the overlay away
              took the "Strip Image Area" box with it, so a coupon with no
              artwork stopped telling its operator it HAS a strip slot, or what
              the crop will do to an upload — which is the one thing the band is
              previewed for. The event ticket kept its copy for this reason and
              the coupon lost its; same mistake, one branch over. */}
          {!deviceOnly && (
            <div
              data-testid="wallet-strip"
              className="w-full surface-tertiary flex items-center justify-center"
              style={{ aspectRatio: getStripAspect(config) }}
            >
              <span className="body-3-regular font-tertiary">Strip Image Area</span>
            </div>
          )}
          {config.primaryFields && config.primaryFields.length > 0 && (
            <div className="pt-3 pb-1" style={passInsetX}>
              <FieldRow
                fields={config.primaryFields}
                certaintyOf={certaintyOfPrimary}
                currentLanguage={currentLanguage}
                colors={colors}
                valueClassName="text-xl font-bold"
                visibleCount={visible.primary}
              />
            </div>
          )}
        </>
      )}

      <div className="pt-3" />

      {config.secondaryFields && config.secondaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.secondaryFields}
            certaintyOf={certaintyOfSecondary}
            currentLanguage={currentLanguage}
            colors={colors}
          visibleCount={visible.secondary}
          />
        </div>
      )}

      {config.auxiliaryFields && config.auxiliaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.auxiliaryFields}
            certaintyOf={certaintyOfAuxiliary}
            currentLanguage={currentLanguage}
            colors={colors}
            visibleCount={visible.auxiliary}
          />
        </div>
      )}

      <BarcodeBlock config={config} colors={colors} />
      {/* `description` is metadata (add-sheet title + VoiceOver), never on face. */}
    </PassCard>
  );
}
